import React, { useState } from 'react';
import { StarRating } from './StarRating';
import { useAuth } from '../../auth/AuthContext';
import { reviewService } from '../../../core/services/firebase/reviewService';
import { reviewSchema } from '../../../core/utils/validation/reviewSchema';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

interface ReviewFormProps {
  tenantId: string;
  onSuccess?: () => void;
}

export const ReviewForm: React.FC<ReviewFormProps> = ({ tenantId, onSuccess }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!user) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <p className="text-blue-700 mb-4">Você precisa estar logado para avaliar esta empresa.</p>
        <button 
          onClick={() => window.location.href = '/login'}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Fazer Login
        </button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const data = {
      tenantId,
      userId: user.uid,
      userName: user.displayName || 'Pescador Anônimo',
      userPhotoURL: user.photoURL || '',
      rating,
      title,
      comment,
      wouldRecommend: rating >= 4,
    };

    const validation = reviewSchema.safeParse(data);
    if (!validation.success) {
      setError(validation.error.issues[0].message);
      setIsSubmitting(false);
      return;
    }

    try {
      await reviewService.createReview(validation.data);
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError('Ocorreu um erro ao enviar sua avaliação. Tente novamente.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-green-800 mb-2">Avaliação Enviada!</h3>
        <p className="text-green-700">Obrigado por compartilhar sua experiência com a nossa comunidade.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-xl p-6 shadow-sm">
      <h3 className="text-xl font-bold text-gray-800 mb-6 text-center md:text-left">Deixe sua Avaliação</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">Sua nota</label>
          <div className="flex justify-center md:justify-start">
            <StarRating 
              rating={rating} 
              editable 
              onChange={setRating} 
              size={32} 
            />
          </div>
          {rating === 0 && <p className="text-xs text-gray-400 mt-1">Selecione de 1 a 5 estrelas</p>}
        </div>

        <div>
          <label htmlFor="title" className="block text-sm font-bold text-gray-700 mb-2">Título (opcional)</label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Resuma sua experiência (ex: Pescaria fantástica!)"
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none transition-all"
          />
        </div>

        <div>
          <label htmlFor="comment" className="block text-sm font-bold text-gray-700 mb-2">Seu comentário</label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder="Conte-nos como foi sua pescaria, o atendimento, as instalações..."
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none transition-all resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">Mínimo de 10 caracteres.</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || rating === 0}
          className={`
            w-full bg-brand-orange text-white font-black py-3 rounded-lg transition-all
            ${isSubmitting || rating === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600 shadow-md hover:shadow-lg'}
          `}
        >
          {isSubmitting ? 'Enviando...' : 'Publicar Avaliação'}
        </button>
      </form>
    </div>
  );
};
